<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import {
  inputsStep2CL,
  inputsStep3CL,
  inputsStep4CL,
  inputsStep4Paqueteria,
  inputsStep4Maritimo,
} from './utils/inputsForm';
import { useToast } from '../../composables/useToast';
import { useMessage } from '../../composables/useMessage';
import { IFinalCalculation } from '../../helpers/finalCalculationAR';
import { formatterRUT, wrongDepartureDateTime } from '../../helpers';
import { validationRut } from '../../helpers/validationRUT';
import {
  findClientByTributaryCode,
  findCompaniesGPS,
  findTypesContainers,
  createOrUpdateClient,
  createPolicy,
  getPricing,
  createPolicyForTrygerLatam,
  createPolicyPackage,
  createPolicyMaritime,
  findCountries
} from '../../repositories/landing.repository';
import GeoRefChileRepository from '../../repositories/geoRefChile.repository';
import { IRegion } from '../../repositories/geoRefChile.repository';
import useClipboard from 'vue-clipboard3';
import moment from 'moment';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();
const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();
const { toClipboard } = useClipboard();

const props = defineProps({
  dataForm1: {
    type: Object,
    required: true,
  },
});

const fieldsStep2: any = ref(inputsStep2CL());
const fieldsStep3: any = ref(inputsStep3CL());
const fieldsStep4: any = ref(inputsStep4CL());
const inputsHidden = ref<boolean>(true);
const urlPayment = ref<string | null>(null);
const dataForm: any = ref({});
const refFormClientData = ref<any>(null);
const refFormTravelData = ref<any>(null);
const loading = ref<boolean>(false);
const regionesCHI = ref<IRegion[]>([]);
const finalCalculation = ref<IFinalCalculation | null>(null);

const emit = defineEmits<{
  (e: 'firstView'): void;
}>();

onMounted(async () => {
  if (!props.dataForm1) {
    inputsHidden.value = true;
    return emit('firstView');
  }
  if (props.dataForm1.typePolicieTransport === 'Paqueteria') {
    fieldsStep4.value = inputsStep4Paqueteria();
  }
  if (props.dataForm1.typePolicieTransport === 'Maritimo') {
    fieldsStep4.value = inputsStep4Maritimo();
  }
});

onMounted(async () => {
  const regiones = await GeoRefChileRepository.getRegiones();
  const countries = await findCountries();

  regionesCHI.value = regiones;
  const comapaniesGPS = await findCompaniesGPS();
  const typesContainers = await findTypesContainers();
  fieldsStep3.value = fieldsStep3.value.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
    }
    if (field.key === 'region') {
      field.options = regiones;
    }
    return field;
  });
  fieldsStep4.value = fieldsStep4.value.map((field: any) => {
    if (field.key === 'destinationRegion') {
      field.options = regiones;
    }
    if (field.key === 'originRegion') {
      field.options = regiones;
    }
    return field;
  });
  fieldsStep4.value = fieldsStep4.value.map((field: any) => {
    if (field.key === 'containerType') {
      field.options = typesContainers;
    }
	if (field.key === 'countryDeparture') {
      field.options = countries;
    }
    if (field.key === 'countryArrival') {
      field.options = countries;
    }
    return field;
  });
});

onUnmounted(() => {
  fieldsStep3.value = inputsStep3CL().map((e) => (e.value = null));
  fieldsStep4.value = inputsStep4CL().map((e) => (e.value = null));
  inputsHidden.value = true;
  dataForm.value = {};
});

const handleOnSubmitRUT = async (e: any) => {
  // 19876543-0 42.245.781-K
  const isValidRUT = validationRut(e.items.rut);
  if (!e.isFormValid || !isValidRUT) {
    return (fieldsStep2.value[0].errors = ['RUT es invalido']);
  }
  const rutWithoutSpecialChars = e.items.rut.replace(/[-.]/g, '');
  const { client } = await findClientByTributaryCode(rutWithoutSpecialChars);

  if (client) {
    dataForm.value = { client };
  }

  if (client) {
    const updatesClientInputs = [
      { key: 'tradeName', value: client.tradeName },
      { key: 'email', value: props?.dataForm1?.email ?? null },
      { key: 'phone1', value: client.phone1 },
      {
        key: 'companyGPSId',
        value: {
          id: client.companyGPS?.id,
          nombre: client.companyGPS?.description,
        },
      },
      { key: 'address', value: client.address },
      { key: 'region', value: { nombre: client.regionProvince } },
      { key: 'comuna', value: { nombre: client.location } },
      { key: 'postCode', value: client.postCode },
    ];

    updateFieldsInArray(fieldsStep3.value, updatesClientInputs, null);
  }
  const updatesTravelInputs = [
    {
      key: 'assetId',
      value: props?.dataForm1?.asset?.nombre ?? null,
    },
    {
      key: 'indemnificationLimit',
      value: props?.dataForm1?.indemnificationLimit ?? null,
    },
    {
      key: 'indemnificationLimit-calc',
      value: `${props?.dataForm1?.endPrice} + IVA`,
    },
  ];

  updateFieldsInArray(fieldsStep4.value, updatesTravelInputs, null);
  inputsHidden.value = false;

  if (!client) {
    const updatesClientInputs = [
      { key: 'email', value: props?.dataForm1?.email ?? null },
    ];
    updateFieldsInArray(fieldsStep3.value, updatesClientInputs, null);

    dataForm.value = { client: { rut: e.items.rut } };
  } else {
    finalCalculation.value = await getPricing(
      props?.dataForm1?.indemnificationLimit,
      e.items.rut,
      client.regionProvince!,
    );
  }
};

const handleGetCities = async () => {
  const { items } = await refFormClientData.value.getValues();
  const comunas = await GeoRefChileRepository.getComunas(items.region.id);
  await refFormClientData.value.setValue({ key: 'comuna', value: null });

  const preservedValues = {
    tradeName: items.tradeName,
    email: items.email,
    phone1: items.phone1,
    companyGPSId: items.companyGPSId,
    address: items.address,
    region: items.region,
  };

  updateFieldsInArray(
    fieldsStep3.value,
    [{ key: 'comuna', options: comunas, value: null }],
    preservedValues,
  );

  finalCalculation.value = await getPricing(
    props?.dataForm1?.indemnificationLimit,
    dataForm.value.client.rut,
    items.region.nombre,
  );
};

const handleSelectChange = async (e: any) => {
  const updateFields: {
    key: string;
    disabled?: boolean;
    'validations.rules.required'?: boolean;
    options?: any[];
  }[] = [];

  const updateCommonFields = (key: string, isEnabled: boolean) => {
    updateFields.push(
      { key, disabled: !isEnabled },
      { key, 'validations.rules.required': isEnabled },
    );
  };

  switch (e.input.key) {
    case 'box-trailer':
      updateCommonFields('trailerPlate', !!e.value.id);
      break;

    case 'originRegion':
    case 'destinationRegion':
      const cities = await GeoRefChileRepository.getComunas(e.value.id);

      const cityKey =
        e.input.key === 'originRegion' ? 'originComuna' : 'destinationComuna';

      await refFormTravelData.value.setValue({ key: cityKey, value: null });
      updateFieldsInArray(
        fieldsStep4.value,
        [{ key: cityKey, options: cities, disabled: false }],
        null,
      );
      return;
    default:
      ['stop1', 'stop2', 'stop3'].forEach((stop) =>
        updateCommonFields(stop, !!e.value.id),
      );
      break;
  }

  updateFieldsInArray(fieldsStep4.value, updateFields, null);
};

const updateFieldsInArray = (
  array: any,
  updates: any,
  preservedValues: any,
) => {
  for (const update of updates) {
    const field = array.find((field: any) => field.key === update.key);
    if (field) {
      if ('type' in update) {
        field.type = update.type;
      }
      if ('options' in update) {
        field.options = update.options;
      }
      if ('value' in update) {
        field.value = update.value;
      }
      if ('disabled' in update) {
        field.disabled = update.disabled;
      }
    }
  }

  if (preservedValues) {
    for (const key in preservedValues) {
      const field = array.find((field: any) => field.key === key);
      if (field) {
        field.value = preservedValues[key];
      }
    }
  }
};

const handleOnSubmitPolicies = ({ items, isFormValid }: any) => {
  if (props.dataForm1.typePolicieTransport === 'Paqueteria') {
    handleOnSubmitPoliciesPaqueteria({ items, isFormValid });
  } else if (props.dataForm1.typePolicieTransport === 'Terrestre') {
    handleOnSubmitPoliciesTerrestre({ items, isFormValid });
  } else {
    handleOnSubmitPoliciesMaritime({ items, isFormValid });
  }
};

const handleCreateUpdateClient = async () => {
  const clientForm = await refFormClientData.value.getValues();

  if (!clientForm.isFormValid) {
    return showMessage(t('nueva_operacion.nueva_contratacion_cl.el_formulario_debe_estar_completo'), 'error');
  }

  if (clientForm.items.tradeName.split(' ').length < 2) {
    return showMessage(
	  t('nueva_operacion.nueva_contratacion_cl.debe_proporcionar_al_menos_un_nombre'),
      'error',
    );
  }

  const createOrUpdateClientData = {
    tradeName: clientForm.items.tradeName,
    address: clientForm.items.address,
    email: clientForm.items.email,
    phone1: clientForm.items.phone1,
    location: clientForm.items.comuna.nombre,
    postCode: clientForm.items.postCode,
    rut: dataForm.value.client.rut.replace(/[-.]/g, ''),
    regionProvince: clientForm.items.region.nombre,
    companyGPSId: clientForm.items.companyGPSId.id,
    urlDomain: 'CL',
  };

  try {
    return await createOrUpdateClient(
      dataForm.value.client.id,
      createOrUpdateClientData,
      'CL',
    );
  } catch (error) {
    loading.value = false;
    showMessage('Ocurrió un Error.', 'error');
  }
};

const handleOnSubmitPoliciesTerrestre = async (formData: any) => {
  const travelForm = formData;
  const clientForm = await refFormClientData.value.getValues();

  if (!travelForm.isFormValid) {
    return showMessage(t('nueva_operacion.nueva_contratacion_cl.debe_proporcionar_al_menos_un_nombre'), 'error');
  }

  if (
    wrongDepartureDateTime(
      travelForm.items.startDate,
      travelForm.items.startTime,
      'cl',
    )
  ) {
    return showMessage(
	  t('nueva_operacion.nueva_contratacion_cl.la_hora_de_salida_no_puede_ser_menor'),
      'error',
    );
  }

  const travelStartDate = new Date(`${travelForm.items.startDate}T${travelForm.items.startTime}`);
  const travelEndDate = new Date(`${travelForm.items.endDate}T${travelForm.items.endTime}`);
  const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
  const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

  const createPolicyData: any = {
    tradeName: clientForm.items.tradeName,
    assetId: props.dataForm1.asset.id,
    phone1: clientForm.items.phone1,
    typeOfMerchandise: props.dataForm1.asset.nombre,
    indemnificationLimit: travelForm.items.indemnificationLimit,
    originRegionProvince: travelForm.items.originRegion.nombre,
    destinationRegionProvince: travelForm.items.destinationRegion.nombre,
    originLocation: travelForm.items.originComuna.nombre ?? travelForm.items.originComuna,
    destinationLocation: travelForm.items.destinationComuna.nombre ?? travelForm.items.destinationComuna,
    stop1: travelForm.items.stop1,
    stop2: travelForm.items.stop2,
    stop3: travelForm.items.stop3,
    trailerPlate: travelForm.items.trailerPlate,
    truckPlate: travelForm.items.truckPlate,
    driverName: travelForm.items.driverName,
    documentDescription: travelForm.items.documentDescription,
    driverCellphone: travelForm.items.driverCellphone,
    driverEmail: travelForm.items.driverEmail,
    travelStartDate: fechaISOstart,
    travelEndDate: fechaISOend,
    hasStops: travelForm.items.stops.nombre,
    companyGPSId: clientForm.items.companyGPSId.id,
    document: travelForm.items.document?.length
      ? travelForm.items.document[0].image
      : null,
    clientName: clientForm.items.tradeName,
    tributaryCode: formatterRUT(dataForm.value.client.rut),
    address: clientForm.items.address,
    postalCode: clientForm.items.postCode,
    location: clientForm.items.comuna.nombre,
    regionProvince: clientForm.items.region.nombre,
    email: clientForm.items.email,
    endPrice: finalCalculation?.value?.primaTotal,
	isAuth: true,
  };

  try {
    loading.value = true;

    const client = await handleCreateUpdateClient();
    const urlForPayment = await createPolicyForTrygerLatam(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'CL',
    );

    loading.value = false;
    if (urlForPayment) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_cl.operacion_registrada_con_exito'));
      urlPayment.value = urlForPayment;
    }
  } catch (error) {
    loading.value = false;
    showMessage(t('global.ocurrio_un_error'), 'error');
  }
};

const handleOnSubmitPoliciesPaqueteria = async (formData: any) => {
  const clientForm = await refFormClientData.value.getValues();

  if (!formData.isFormValid) {
    return showMessage(t('nueva_operacion.nueva_contratacion_cl.el_formulario_debe_estar_completo'), 'error');
  }

  try {
    loading.value = true;

    const client = await handleCreateUpdateClient();

    const packageNumbersCleaned = formData.items.packageNumbers
      .split(',')
      .map((e: string) => e.trim())
      .filter((e: string) => e !== '');

    const createPolicyData = {
      tradeName: clientForm.items.tradeName,
      rut: dataForm.value.client.rut,
      indemnificationLimit: formData.items.indemnificationLimit,
      companyGPSId: clientForm.items.companyGPSId.id,
      packageNumbers: JSON.stringify(packageNumbersCleaned),
	  isAuth: true,
    };

    const newPolicyId = await createPolicyPackage(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'CL',
    );

    loading.value = false;

    if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_cl.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
    showMessage(`${t('global.ocurrio_un_error')}. ${error.message}`, 'error');
    loading.value = false;
  }
};

const handleOnSubmitPoliciesMaritime = async (formData: any) => {
  const clientForm = await refFormClientData.value.getValues();

  if (!formData.isFormValid) {
    return showMessage(t('nueva_operacion.nueva_contratacion_cl.el_formulario_debe_estar_completo'), 'error');
  }

  if (
    formData.items.destinationCityZipCodeIdCalc === 'CP INVALIDO' ||
    formData.items.sourceCityZipCodeIdCalc === 'CP INVALIDO'
  ) {
    return showMessage(t('nueva_operacion.nueva_contratacion_cl.codigo_postal_no_valido'), 'error');
  }

  try {
    loading.value = true;

    const client = await handleCreateUpdateClient();

    const createPolicyData = {
      tradeName: clientForm.items.tradeName,
      rut: dataForm.value.client.rut,
      assetId: props.dataForm1.asset.id,
      travelEndDate: formData.items.travelEndDate,
      travelStartDate: formData.items.travelStartDate,
      indemnificationLimit: formData.items.indemnificationLimit,
      companyGPSId: clientForm.items.companyGPSId.id,
      shippingCompany: formData.items.shippingCompany,
      portDeparture: formData.items.portDeparture,
      portArrival: formData.items.portArrival,
      typeContainerId: formData.items.containerType.id,
      containerNumber: formData.items.containerNumber,
	  countryIdDeparture: formData.items.countryDeparture.id,
	  countryIdArrival: formData.items.countryArrival.id,
	  isAuth: true,
    };

    const newPolicyId = await createPolicyMaritime(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
      'CL',
    );

    loading.value = false;

    if (newPolicyId) {
      $toast?.success(t('nueva_operacion.nueva_contratacion_cl.operacion_registrada_con_exito'));
      emit('firstView');
    }
  } catch (error: any) {
    showMessage(`${t('global.ocurrio_un_error')}. ${error.message}`, 'error');
    loading.value = false;
  }
};

const handleAllowEndDate = async (e: any) => {
  const isTerrestre = props.dataForm1.typePolicieTransport === 'Terrestre';
  const key = isTerrestre ? 'endDate' : 'travelEndDate';

  const field = fieldsStep4.value.find((field: any) => field.key === key);
  if (field) {
    field.disabled = !e.value;
    field.min = new Date(`${e.value}T00:00:00`);
  }

  await refFormTravelData.value.setValue({ key, value: null });
};

const copyToClipboard = async (url: string) => {
  try {
    await toClipboard(url);
  } catch (e) {
    console.error(e);
  }
};

const goToinit = async () => {
  emit('firstView');
};
</script>

<template>
  <Layout :title="t('nueva_operacion.nueva_contratacion_cl.nueva_contratacion')" :textColor="'color-secondary'">
    <template #layout>
      <Form
        :inputs="fieldsStep2"
        @onSubmit="handleOnSubmitRUT"
        v-if="inputsHidden"
      >
        <template #buttons>
          <div class="d-flex">
            <CButton background="background-blue" style="font-size: 1rem">
              <template #content> {{ t('nueva_operacion.nueva_contratacion_cl.comprobar_rut') }} </template>
            </CButton>
          </div>
        </template>
      </Form>
      <template v-if="!inputsHidden">
        <h4 class="color-pink">
		   {{ t('nueva_operacion.nueva_contratacion_cl.datos_del_cliente') }} ({{ t('nueva_operacion.nueva_contratacion_cl.rut') }}: {{ dataForm?.client?.rut ?? null }})
        </h4>
        <Form
          ref="refFormClientData"
          :inputs="fieldsStep3"
          @select="handleGetCities"
        ></Form>
        <h4 class="my-3 color-pink">{{ t('nueva_operacion.nueva_contratacion_cl.datos_del_viaje') }}</h4>
        <Form
          ref="refFormTravelData"
          :inputs="fieldsStep4"
          @onSubmit="handleOnSubmitPolicies"
          @select="handleSelectChange"
          @datePicker="handleAllowEndDate"
        >
          <template #buttons>
            <div class="my-2" v-if="messageHTML" v-html="messageHTML" />
            <div v-if="finalCalculation" class="container p-0 my-2">
              <b-card class="rounded-4">
                <h2 class="text-center text-primary">
					{{ t('nueva_operacion.nueva_contratacion_cl.calculo_de_prima_fina_y_pago') }}
                </h2>
                <b-row class="mt-3">
                  <b-col>
                    <b-list-group>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-light"
                      >
                        <span><strong>{{ t('nueva_operacion.nueva_contratacion_cl.monto_imponible') }}:</strong></span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.montoImponible }}</span
                        >
                      </b-list-group-item>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('nueva_operacion.nueva_contratacion_cl.taxes') }}</span>
                      </b-list-group-item>

                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('nueva_operacion.nueva_contratacion_cl.iva') }}:</span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.IVA }}</span
                        >
                      </b-list-group-item>

                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-primary text-white"
                      >
                        <span><strong>{{ t('nueva_operacion.nueva_contratacion_cl.prima_total') }}:</strong></span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.primaTotal }}</span
                        >
                      </b-list-group-item>
                    </b-list-group>
                  </b-col>
                </b-row>
              </b-card>
            </div>
            <div v-if="urlPayment" class="container p-0 my-2">
              <b-card class="rounded-4">
                <h2 class="text-center text-primary">{{ t('nueva_operacion.nueva_contratacion_cl.url_de_pago') }}</h2>
                <b-row class="mt-3">
                  <b-col>
                    <b-list-group>
                      <b-list-group-item class="bg-light">
                        <span><strong>{{ t('nueva_operacion.nueva_contratacion_cl.url') }}: </strong></span>
                        <span class="font-weight-bold">{{ urlPayment }}</span>
                        <button
                          class="background-blue color-white text-uppercase mx-2 px-2 border-0 rounded-4"
                          @click="copyToClipboard(urlPayment)"
                          type="button"
                        >
							{{ t('nueva_operacion.nueva_contratacion_cl.copiar_url') }}
                        </button>
                        <button
                          class="background-blue color-white text-uppercase mx-2 px-2 border-0 rounded-4"
                          @click="goToinit()"
                          type="button"
                        >
							{{ t('nueva_operacion.nueva_contratacion_cl.ir_a_inicio') }}
                        </button>
                      </b-list-group-item>
                    </b-list-group>
                  </b-col>
                </b-row>
              </b-card>
            </div>
            <CButton
              class="p-4 w-100 px-2"
              background="background-blue"
              style="font-size: 1.5rem"
              v-if="!urlPayment"
            >
              <template #content>
                <div>
                  <BSpinner v-if="loading" />
                  <span v-else>{{ t('nueva_operacion.nueva_contratacion_cl.registrar_poliza') }}</span>
                </div>
              </template>
            </CButton>
          </template>
        </Form>
      </template>
    </template>
  </Layout>
</template>
